import { ChevronRightIcon, SearchIcon } from '@chakra-ui/icons';
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import PageLoader from '../../../components/layout/page-loader/PageLoader';
import Pagination from '../../../components/pagination/Pagination';
import { usePermissions } from '../../../hooks/usePermissions';
import { getPlansList } from '../../../services/plans/listPlans';
import { updateGrid } from '../../../services/plans/updateGrid';
import { InsurancePlan } from '../../../types/plans/types';
import {
  AgeRangeCatalog,
  coerceRangeForBackend,
  isValidRange,
  labelToRange,
  parseBackendAgeString,
  rangeToLabel,
} from '../../../utils/ageRanges';

interface ProviderCredentialForm {
  id?: string;
  practiceId: string;
  providerId: string;
  plan_id: string;
  credentialStatus: string;
  value: string;
  minimumAge: number | null;
  maximumAge: number | null;
}

const CREDENTIAL_STATUS_OPTIONS = ['Pending', 'Credentialed'];

const PATIENT_ACCEPTANCE_OPTIONS = [
  'Established Patients',
  'Schedule Allowed',
  'PCP Match',
  'No Status',
  'Not Participating',
];

// Age ranges now come from AgeRangeCatalog SSOT

const EditPlanGroup = () => {
  const navigate = useNavigate();
  const { id: planId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const toast = useToast();

  // Get user permissions
  const { canEditInsurance, isAdmin } = usePermissions();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [savingIndex, setSavingIndex] = useState<number | null>(null);
  const [dirtyRows, setDirtyRows] = useState<Set<number>>(new Set());
  const [plan, setPlan] = useState<InsurancePlan | undefined>(
    undefined
  );
  const [allPlans, setAllPlans] = useState<InsurancePlan[]>([]);
  const [providerCredentials, setProviderCredentials] = useState<
    ProviderCredentialForm[]
  >([]);

  // Pagination and search state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCredentials, setFilteredCredentials] = useState<
    ProviderCredentialForm[]
  >([]);

  // Deep-linking state
  const [highlightedRowId, setHighlightedRowId] = useState<
    string | null
  >(null);
  const [isHighlighted, setIsHighlighted] = useState(false);

  const fetchPlan = useCallback(async () => {
    if (planId) {
      setIsLoading(true);
      try {
        const { data } = await getPlansList(
          null,
          { insuranceIds: planId },
          1000 // Fetch all records for this plan group
        );
        const list = data?.insurancePlans || [];
        if (list.length > 0) {
          // Store all plans for provider/practice lookup
          setAllPlans(list);
          // Use first row for header context
          setPlan(list[0]);

          // Map ALL provider rows in the plan group
          const mapAge = (age?: string | null) =>
            parseBackendAgeString(age ?? null);

          const creds: ProviderCredentialForm[] = list.map((row) => {
            const p = row.plans?.[0];

            // Prioritize numeric fields if available, fallback to parsing age string
            let min: number | null;
            let max: number | null;

            if (
              p?.minimumAge !== undefined ||
              p?.maximumAge !== undefined
            ) {
              // Use the numeric fields directly if they exist
              min = p?.minimumAge ?? null;
              max = p?.maximumAge ?? null;
            } else {
              // Fallback to parsing the age string
              const parsed = mapAge(p?.age);
              min = parsed.min;
              max = parsed.max;
            }

            return {
              id: p?.id,
              practiceId: row.practice.id,
              providerId: row.provider.id,
              plan_id: planId,
              credentialStatus: p?.credentialstatus || 'Pending',
              value: p?.value || 'No Status',
              minimumAge: min,
              maximumAge: max,
            };
          });

          setProviderCredentials(creds);
        }
      } catch (error: unknown) {
        console.error('Failed to fetch plan:', error);
        toast({
          title: 'Error loading plan',
          description: 'Failed to load plan data. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    }
  }, [planId, toast]);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  // Filter and paginate credentials
  useEffect(() => {
    let filtered = providerCredentials;

    if (searchQuery.trim()) {
      filtered = providerCredentials.filter((credential) => {
        // Find the provider and practice names for this credential
        const providerRow = allPlans.find(
          (p) => p.provider.id === credential.providerId
        );
        const practiceRow = allPlans.find(
          (p) => p.practice.id === credential.practiceId
        );

        const providerName = providerRow
          ? `${providerRow.provider.firstName || ''} ${
              providerRow.provider.lastName || ''
            }`.trim()
          : '';
        const practiceName = practiceRow
          ? `${practiceRow.practice.name || ''}`.trim()
          : '';

        const searchLower = searchQuery.toLowerCase();
        return (
          providerName.toLowerCase().includes(searchLower) ||
          practiceName.toLowerCase().includes(searchLower)
        );
      });
    }

    setFilteredCredentials(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  }, [providerCredentials, searchQuery, allPlans]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredCredentials.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentPageCredentials = filteredCredentials.slice(
    startIndex,
    endIndex
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  // Deep-linking logic
  useEffect(() => {
    const providerId = searchParams.get('providerId');
    const practiceId = searchParams.get('practiceId');
    const keepHighlight = searchParams.get('keepHighlight');

    // Support both old rowId format and new providerId/practiceId format
    const rowId =
      searchParams.get('rowId') ||
      (providerId && practiceId
        ? `${providerId}-${practiceId}`
        : null);
    const highlight =
      searchParams.get('highlight') === 'true' ||
      keepHighlight === 'true';

    if (rowId && highlight) {
      setHighlightedRowId(rowId);
      setIsHighlighted(true);

      // Find the row in the filtered credentials and navigate to its page
      const targetIndex = filteredCredentials.findIndex(
        (credential) =>
          `${credential.providerId}-${credential.practiceId}` ===
          rowId
      );

      if (targetIndex !== -1) {
        const targetPage = Math.ceil((targetIndex + 1) / pageSize);
        setCurrentPage(targetPage);

        // Scroll to the row after a short delay to ensure it's rendered
        setTimeout(() => {
          const rowElement = document.querySelector(
            `[data-row-id="${rowId}"]`
          );
          if (rowElement) {
            rowElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }, 100);
      }

      // Clear highlight after 3 seconds
      setTimeout(() => {
        setIsHighlighted(false);
        setHighlightedRowId(null);
      }, 3000);
    }
  }, [searchParams, filteredCredentials, pageSize]);

  const handleCredentialStatusChange = (
    providerIndex: number,
    value: string
  ) => {
    setProviderCredentials((prev) =>
      prev.map((cred, index) =>
        index === providerIndex
          ? { ...cred, credentialStatus: value }
          : cred
      )
    );
    setDirtyRows((prev) => new Set(prev).add(providerIndex));
  };

  const handlePatientAcceptanceChange = (
    providerIndex: number,
    value: string
  ) => {
    setProviderCredentials((prev) =>
      prev.map((cred, index) =>
        index === providerIndex ? { ...cred, value } : cred
      )
    );
    setDirtyRows((prev) => new Set(prev).add(providerIndex));
  };

  const handleAgeRangeChange = (
    providerIndex: number,
    type: 'min' | 'max',
    value: number | null
  ) => {
    setProviderCredentials((prev) =>
      prev.map((cred, index) =>
        index === providerIndex
          ? {
              ...cred,
              [type === 'min' ? 'minimumAge' : 'maximumAge']: value,
            }
          : cred
      )
    );
    setDirtyRows((prev) => new Set(prev).add(providerIndex));
  };

  const handleSaveRow = async (rowIndex: number) => {
    if (!canEditInsurance() && !isAdmin()) {
      toast({
        title: 'Access Denied',
        description: 'You do not have permission to edit this plan.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const credential = providerCredentials[rowIndex];
    if (!credential) return;

    // Optional: guard against missing id if backend requires it
    if (!credential.id) {
      console.warn('Missing grid id for update.');
      // proceed anyway if API supports lookup by composite keys
    }

    // Validate age range
    const rangeValid = isValidRange({
      min: credential.minimumAge,
      max: credential.maximumAge,
    });
    if (!rangeValid) {
      toast({
        title: 'Invalid age range',
        description:
          'Minimum age cannot be greater than maximum age and must be within 0–130.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    setSavingIndex(rowIndex);
    setIsSaving(true);
    try {
      const { minimumAge, maximumAge } = coerceRangeForBackend({
        min: credential.minimumAge,
        max: credential.maximumAge,
      });
      await updateGrid({
        ...credential,
        minimumAge,
        maximumAge,
      });

      toast({
        title: 'Row updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });

      // mark row as clean
      setDirtyRows((prev) => {
        const copy = new Set(prev);
        copy.delete(rowIndex);
        return copy;
      });
    } catch (error) {
      console.error('Failed to save grid row:', error);
      toast({
        title: "Couldn't save plan group. Try again.",
        status: 'error',
        duration: 6000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setIsSaving(false);
      setSavingIndex(null);
    }
  };

  const handleCancelRow = (rowIndex: number) => {
    // Reset the row to its original state
    setProviderCredentials((prev) => {
      const originalCredential = allPlans.find(
        (p) => p.provider.id === prev[rowIndex].providerId
      );
      if (originalCredential) {
        const p = originalCredential.plans?.[0];

        // Prioritize numeric fields if available, fallback to parsing age string
        let min: number | null;
        let max: number | null;

        if (
          p?.minimumAge !== undefined ||
          p?.maximumAge !== undefined
        ) {
          // Use the numeric fields directly if they exist
          min = p?.minimumAge ?? null;
          max = p?.maximumAge ?? null;
        } else {
          // Fallback to parsing the age string
          const mapAge = (age?: string | null) =>
            parseBackendAgeString(age ?? null);
          const parsed = mapAge(p?.age);
          min = parsed.min;
          max = parsed.max;
        }

        return prev.map((cred, index) =>
          index === rowIndex
            ? {
                ...cred,
                credentialStatus: p?.credentialstatus || 'Pending',
                value: p?.value || 'No Status',
                minimumAge: min,
                maximumAge: max,
              }
            : cred
        );
      }
      return prev;
    });

    // Remove from dirty rows
    setDirtyRows((prev) => {
      const copy = new Set(prev);
      copy.delete(rowIndex);
      return copy;
    });

    // Show cancel toast
    toast({
      title: 'Edit cancelled',
      description: 'Changes have been reverted',
      status: 'info',
      duration: 3000,
      isClosable: true,
      position: 'top-right',
    });
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (!plan) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        height="50vh"
        gap="1rem"
      >
        <Text fontSize="1.25rem" fontWeight="600" color="red.500">
          Plan not found
        </Text>
        <Button variant="outline" onClick={() => navigate('/plans')}>
          Back to Plans
        </Button>
      </Flex>
    );
  }

  // Check permissions - redirect if user doesn't have access
  if (!canEditInsurance() && !isAdmin()) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        height="50vh"
        gap="1rem"
      >
        <Text fontSize="1.25rem" fontWeight="600" color="red.500">
          Access Denied
        </Text>
        <Text color="gray.600" textAlign="center">
          You do not have permission to edit insurance plans.
        </Text>
        <Button variant="outline" onClick={() => navigate('/plans')}>
          Back to Plans
        </Button>
      </Flex>
    );
  }

  const planName =
    plan.plans?.[0]?.insurance?.friendlyName || 'Unknown Plan';

  return (
    <>
      <Helmet>
        <title>Edit plan group (bulk) - {planName}</title>
      </Helmet>

      <Breadcrumb
        mt="2rem"
        ml="2rem"
        mr="2rem"
        mb="1.62rem"
        separator={<ChevronRightIcon color="gray.500" />}
      >
        <BreadcrumbItem onClick={() => navigate('/')}>
          <BreadcrumbLink>
            <Text
              fontSize="0.875rem"
              fontWeight="400"
              lineHeight="1.25rem"
              color="gray.700"
            >
              Home
            </Text>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem onClick={() => navigate('/plans')}>
          <BreadcrumbLink>
            <Text
              fontSize="0.875rem"
              fontWeight="400"
              lineHeight="1.25rem"
              color="gray.700"
            >
              Plans
            </Text>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem onClick={() => navigate(`/plans/${planId}`)}>
          <BreadcrumbLink>
            <Text
              fontSize="0.875rem"
              fontWeight="400"
              lineHeight="1.25rem"
              color="gray.700"
            >
              {planName}
            </Text>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink href="#">
            <Text
              fontSize="0.875rem"
              fontWeight="400"
              lineHeight="1.25rem"
              color="gray.700"
            >
              Edit plan group (bulk)
            </Text>
          </BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <Flex
        direction="column"
        ml="2rem"
        mr="2rem"
        gap="1.5rem"
        minH="28rem"
        maxH="80vh"
        overflow="auto"
      >
        {/* Header */}
        <Flex
          padding="1.5rem"
          background="background.surfaceTeritiary"
          borderRadius="0.5rem"
          alignItems="center"
          justifyContent="space-between"
        >
          <Flex direction="column" gap="0.5rem">
            <Text
              fontSize="1.5rem"
              fontWeight="700"
              lineHeight="133%"
              color="text.primary"
            >
              Edit plan group
            </Text>
            <Text
              fontSize="1.125rem"
              fontWeight="600"
              color="text.secondary"
            >
              {planName}
            </Text>
            <Flex alignItems="center" gap="0.5rem">
              <Text
                fontSize="0.875rem"
                fontWeight="500"
                color="text.secondary"
              >
                Plan Group:
              </Text>
              <Text
                fontSize="0.875rem"
                fontWeight="600"
                color="blue.500"
                bg="blue.50"
                px="0.5rem"
                py="0.25rem"
                borderRadius="0.25rem"
                data-testid="edit-plan-group-subtitle"
              >
                {planName}
              </Text>
            </Flex>
          </Flex>
        </Flex>

        {/* Helper Text Section */}
        <Box
          p="1rem"
          bg="blue.50"
          borderRadius="0.5rem"
          border="1px solid"
          borderColor="blue.200"
        >
          <Text
            fontSize="0.875rem"
            fontWeight="500"
            color="blue.700"
            mb="0.5rem"
          >
            How to Edit Provider Insurance Plans
          </Text>
          <Text fontSize="0.8rem" color="blue.600" lineHeight="1.4">
            • Click on any status badge to change the credential
            status, scheduling value, or age range
            <br />• Changes are saved automatically when you click
            "Save" for each row
          </Text>
        </Box>

        {/* Providers Section */}
        <Flex direction="column" gap="1rem">
          <Flex justify="space-between" align="center">
            <Text
              fontSize="1.25rem"
              fontWeight="700"
              color="text.secondary"
            >
              Providers ({filteredCredentials.length} of{' '}
              {providerCredentials.length})
            </Text>
            {dirtyRows.size > 0 && (
              <Text
                fontSize="0.875rem"
                color="blue.600"
                fontWeight="500"
                bg="blue.50"
                px="0.75rem"
                py="0.25rem"
                borderRadius="0.375rem"
              >
                {dirtyRows.size} row{dirtyRows.size > 1 ? 's' : ''}{' '}
                with unsaved changes
              </Text>
            )}
          </Flex>
          <Text fontSize="0.875rem" color="gray.500" mb="1rem">
            Edit credential status, patient acceptance, and age ranges
            for each provider. Click Save on individual rows to update
            changes.
          </Text>

          {/* Search and Pagination Controls */}
          <Flex justify="space-between" align="center" mb="1rem">
            <InputGroup maxW="20rem">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search providers and practices..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="edit-plan-search"
              />
            </InputGroup>

            <Flex align="center" gap="1rem">
              <Text fontSize="0.875rem" color="gray.600">
                Show:
              </Text>
              <Select
                value={pageSize}
                onChange={(e) =>
                  handlePageSizeChange(Number(e.target.value))
                }
                width="5rem"
                size="sm"
                data-testid="edit-plan-pagination"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </Select>
              <Text fontSize="0.875rem" color="gray.600">
                per page
              </Text>
            </Flex>
          </Flex>

          <Box
            bg="white"
            borderRadius="0.5rem"
            overflow="hidden"
            boxShadow="sm"
            maxH="50vh"
            overflowY="auto"
            data-testid="edit-plan-table-container"
          >
            <Table
              variant="simple"
              bg="white"
              borderRadius="0.5rem"
              overflow="hidden"
            >
              <Thead bg="gray.50">
                <Tr>
                  <Th fontWeight="600" color="gray.700">
                    Provider
                  </Th>
                  <Th fontWeight="600" color="gray.700">
                    Practice
                  </Th>
                  <Th fontWeight="600" color="gray.700">
                    Credential status
                  </Th>
                  <Th fontWeight="600" color="gray.700">
                    Accepting patients?
                  </Th>
                  <Th fontWeight="600" color="gray.700">
                    Age range
                  </Th>
                  <Th fontWeight="600" color="gray.700">
                    Action
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {currentPageCredentials.map(
                  (credential, originalIndex) => {
                    // Find the original index in the full array for dirty tracking
                    const fullArrayIndex =
                      providerCredentials.findIndex(
                        (c) =>
                          c.providerId === credential.providerId &&
                          c.practiceId === credential.practiceId
                      );
                    const rowId = `${credential.providerId}-${credential.practiceId}`;
                    const isRowHighlighted =
                      isHighlighted && highlightedRowId === rowId;

                    return (
                      <Tr
                        key={originalIndex}
                        data-row-id={rowId}
                        data-testid={
                          isRowHighlighted
                            ? 'highlighted-row'
                            : undefined
                        }
                        bg={
                          isRowHighlighted
                            ? 'yellow.100'
                            : dirtyRows.has(fullArrayIndex)
                            ? 'blue.50'
                            : 'white'
                        }
                        borderLeft={
                          isRowHighlighted
                            ? '3px solid'
                            : dirtyRows.has(fullArrayIndex)
                            ? '3px solid'
                            : '3px solid transparent'
                        }
                        borderLeftColor={
                          isRowHighlighted
                            ? 'yellow.400'
                            : dirtyRows.has(fullArrayIndex)
                            ? 'blue.400'
                            : 'transparent'
                        }
                        transition="all 0.3s ease"
                      >
                        <Td>
                          <Text fontWeight="500">
                            {(() => {
                              // Find the correct provider from the original data
                              const providerRow = allPlans.find(
                                (p) =>
                                  p.provider.id ===
                                  credential.providerId
                              );
                              return providerRow
                                ? `${
                                    providerRow.provider.firstName ||
                                    ''
                                  } ${
                                    providerRow.provider.lastName ||
                                    ''
                                  }`.trim()
                                : 'Unknown Provider';
                            })()}
                          </Text>
                        </Td>
                        <Td>
                          <Text
                            fontSize="0.875rem"
                            color="text.secondary"
                          >
                            {(() => {
                              // Find the correct practice from the original data
                              const practiceRow = allPlans.find(
                                (p) =>
                                  p.practice.id ===
                                  credential.practiceId
                              );
                              return practiceRow
                                ? `${
                                    practiceRow.practice.name || ''
                                  }, ${
                                    practiceRow.practice.state || ''
                                  }`.trim()
                                : 'Unknown Practice';
                            })()}
                          </Text>
                        </Td>
                        <Td>
                          <Select
                            value={credential.credentialStatus}
                            onChange={(e) =>
                              handleCredentialStatusChange(
                                fullArrayIndex,
                                e.target.value
                              )
                            }
                            size="sm"
                          >
                            {CREDENTIAL_STATUS_OPTIONS.map(
                              (option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              )
                            )}
                          </Select>
                        </Td>
                        <Td>
                          <Select
                            value={credential.value}
                            onChange={(e) =>
                              handlePatientAcceptanceChange(
                                fullArrayIndex,
                                e.target.value
                              )
                            }
                            size="sm"
                          >
                            {PATIENT_ACCEPTANCE_OPTIONS.map(
                              (option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              )
                            )}
                          </Select>
                        </Td>
                        <Td>
                          {(() => {
                            const selectedLabel = rangeToLabel({
                              min: credential.minimumAge,
                              max: credential.maximumAge,
                            });
                            const matched = AgeRangeCatalog.some(
                              (i) => i.label === selectedLabel
                            );
                            return (
                              <Select
                                value={matched ? selectedLabel : ''}
                                onChange={(e) => {
                                  const label = e.target.value;
                                  try {
                                    const range = labelToRange(label);
                                    handleAgeRangeChange(
                                      fullArrayIndex,
                                      'min',
                                      range.min
                                    );
                                    handleAgeRangeChange(
                                      fullArrayIndex,
                                      'max',
                                      range.max
                                    );
                                  } catch (error) {
                                    console.warn(
                                      'Error updating age range:',
                                      error
                                    );
                                  }
                                }}
                                size="sm"
                              >
                                {!matched && (
                                  <option value="" disabled>
                                    {selectedLabel}
                                  </option>
                                )}
                                {AgeRangeCatalog.map((item) => (
                                  <option
                                    key={item.label}
                                    value={item.label}
                                  >
                                    {item.label}
                                  </option>
                                ))}
                              </Select>
                            );
                          })()}
                        </Td>
                        <Td>
                          <Flex gap="0.5rem">
                            <Button
                              colorScheme="blue"
                              size="sm"
                              onClick={() =>
                                handleSaveRow(fullArrayIndex)
                              }
                              isLoading={
                                savingIndex === fullArrayIndex &&
                                isSaving
                              }
                              loadingText="Saving..."
                              isDisabled={
                                !dirtyRows.has(fullArrayIndex)
                              }
                              variant={
                                dirtyRows.has(fullArrayIndex)
                                  ? 'solid'
                                  : 'outline'
                              }
                              opacity={
                                dirtyRows.has(fullArrayIndex)
                                  ? 1
                                  : 0.6
                              }
                            >
                              {dirtyRows.has(fullArrayIndex)
                                ? 'Save Changes'
                                : 'No Changes'}
                            </Button>
                            {dirtyRows.has(fullArrayIndex) && (
                              <Button
                                size="sm"
                                variant="outline"
                                colorScheme="gray"
                                onClick={() =>
                                  handleCancelRow(fullArrayIndex)
                                }
                              >
                                Cancel
                              </Button>
                            )}
                          </Flex>
                        </Td>
                      </Tr>
                    );
                  }
                )}
              </Tbody>
            </Table>
          </Box>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </Flex>
      </Flex>
    </>
  );
};

export default EditPlanGroup;
